import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, ShoppingBag, ShoppingCart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import CartDrawer from './CartDrawer';
import { apiFetch } from './api';
import { MarketplaceHeader } from '../../components/marketplace/MarketplaceHeader';
import './MarketplaceHome.css';

/**
 * The cart as a real page (session 62d). It was a drawer only — nothing you could land on,
 * bookmark or come back to, and cramped on a phone. Same component underneath, so checkout
 * behaviour is identical.
 *
 * Saved for later sits below the cart, which is where people actually decide.
 */
export default function CartPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSaved = useCallback(() => {
    setLoading(true);
    apiFetch('/wishlist')
      .then((d) => setSaved(d.items || []))
      .catch(() => setSaved([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  const moveToCart = async (p) => {
    // Sold in types? It has to be picked on the product page, not guessed here.
    if ((p.variants?.length || 0) > 0) {
      navigate(`/dashboard/marketplace/product/${p.id}`);
      return;
    }
    try {
      await apiFetch('/cart/items', { method: 'POST', body: JSON.stringify({ product_id: p.id, qty: 1 }) });
      await apiFetch(`/wishlist/${p.id}`, { method: 'DELETE' });
      setSaved((s) => s.filter((x) => x.id !== p.id));
      window.dispatchEvent(new Event('cart-updated'));
    } catch (e) {
      toast.error(e.message || 'Could not move that to your basket');
    }
  };

  const removeSaved = async (p) => {
    try {
      await apiFetch(`/wishlist/${p.id}`, { method: 'DELETE' });
      setSaved((s) => s.filter((x) => x.id !== p.id));
    } catch { /* ignore */ }
  };

  return (
    <div className="mp-root" data-testid="cart-page">
      <MarketplaceHeader savedCount={saved.length} compact />

      <div style={{ padding: '24px 16px 60px' }}>
        <CartDrawer
          mode="page"
          startInCheckout={params.get('checkout') === '1'}
          onClose={() => navigate('/dashboard/marketplace')}
          onOrderSuccess={() => navigate('/dashboard/marketplace/orders')}
        />

        <div style={{ maxWidth: 720, margin: '20px auto 0' }}>
          <div style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
            borderRadius: 16, padding: 18,
          }} data-testid="saved-for-later">
            <p style={{
              display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 4px',
              fontSize: 16, fontWeight: 600, color: '#f5f0e8',
            }}>
              <Heart size={16} color="#ef5a78" /> Saved for later
              {saved.length > 0 && (
                <span style={{ fontSize: 12, color: 'rgba(245,240,232,0.45)', fontWeight: 400 }}>
                  {saved.length} item{saved.length !== 1 ? 's' : ''}
                </span>
              )}
            </p>

            {loading ? (
              <p style={{ fontSize: 13, color: 'rgba(245,240,232,0.4)' }}>Loading…</p>
            ) : saved.length === 0 ? (
              <p style={{ fontSize: 13, color: 'rgba(245,240,232,0.4)', margin: 0 }}
                data-testid="saved-for-later-empty">
                Nothing saved yet. Tap the heart on any product and it waits for you here.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
                {saved.map((p) => (
                  <div key={p.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: 10, borderRadius: 12, background: 'rgba(255,255,255,0.04)',
                  }} data-testid={`saved-item-${p.id}`}>
                    <div style={{
                      width: 52, height: 52, borderRadius: 9, overflow: 'hidden', flexShrink: 0,
                      background: 'rgba(91,181,162,0.10)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', color: 'rgba(91,181,162,0.5)',
                    }}>
                      {p.images?.[0]
                        ? <img src={p.images[0]} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <ShoppingBag size={18} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        onClick={() => navigate(`/dashboard/marketplace/product/${p.id}`)}
                        style={{ margin: 0, fontSize: 13.5, color: '#f5f0e8', cursor: 'pointer' }}
                      >
                        {p.title}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 13, color: '#7dd4c0', fontWeight: 600 }}>
                        ${Number(p.price).toFixed(2)}
                      </p>
                    </div>
                    <button onClick={() => moveToCart(p)} className="mp-card-add"
                      style={{ width: 'auto', margin: 0, padding: '7px 14px' }}
                      data-testid={`saved-move-to-cart-${p.id}`}>
                      <ShoppingCart size={13} /> {(p.variants?.length || 0) > 0 ? 'Choose type' : 'Move to cart'}
                    </button>
                    <button onClick={() => removeSaved(p)}
                      style={{ background: 'none', border: 'none', color: 'rgba(245,240,232,0.4)', cursor: 'pointer' }}
                      data-testid={`saved-remove-${p.id}`} aria-label="Remove from saved">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
